import { CategoryNode } from '../utils/category';
import { GATED_MEASUREMENTS, GatedMeasurement } from './gated-measurements';

/**
 * Predicate that reports whether a given feature-flag key is enabled for the
 * current user. In production this is backed by Backstage's `featureFlagsApi`
 * (`flagKey => featureFlagsApi.isActive(flagKey)`); tests can supply a plain
 * function.
 */
export type IsFlagActive = (flagKey: string) => boolean;

/**
 * Computes the set of check names that must be HIDDEN from the current user
 * because their measurement is gated behind a feature flag the user has not
 * enabled.
 *
 * Gating is matched by `(category, measurement)` against the static gating map
 * and then expanded to the measurement's `checks[]` names, since enforcement is
 * check-name-based downstream.
 */
export function getHiddenCheckNames(
  registryData: CategoryNode[],
  isFlagActive: IsFlagActive,
  gatedMeasurements: GatedMeasurement[] = GATED_MEASUREMENTS,
): Set<string> {
  const hidden = new Set<string>();
  if (gatedMeasurements.length === 0) {
    return hidden;
  }

  for (const gated of gatedMeasurements) {
    // An enabled flag makes its measurement's checks visible — skip it.
    if (isFlagActive(gated.flagKey)) {
      continue;
    }
    // Category and measurement names are editable in the registry database, so a
    // rename would stop the match below and silently expose a gated measurement.
    // Check names come from code, so hiding them here holds through a rename.
    for (const checkName of gated.checks ?? []) {
      hidden.add(checkName);
    }

    for (const category of registryData) {
      if (category.category !== gated.category) {
        continue;
      }
      for (const measurement of category.measurements) {
        if (measurement.name !== gated.measurement) {
          continue;
        }
        for (const check of measurement.checks) {
          hidden.add(check.name);
        }
      }
    }
  }

  return hidden;
}

/**
 * The single canonical "check visible for current user" resolver. Takes the
 * already-computed candidate check list (e.g. the exclude-derived output of
 * `getAllChecks`) and removes the gated checks the user has not enabled.
 *
 * This is an ADDITIONAL subtraction layered onto the existing pipeline, not a
 * replacement: callers pass in `allChecks` (registry checks minus the
 * measurements the user excluded via the table filter) and receive that list
 * minus gated-not-enabled checks. When nothing is gated, the input is returned
 * unchanged.
 */
export function resolveVisibleChecks(
  allChecks: string[],
  registryData: CategoryNode[],
  isFlagActive: IsFlagActive,
  gatedMeasurements: GatedMeasurement[] = GATED_MEASUREMENTS,
): string[] {
  const hidden = getHiddenCheckNames(
    registryData,
    isFlagActive,
    gatedMeasurements,
  );
  if (hidden.size === 0) {
    return allChecks;
  }
  return allChecks.filter(name => !hidden.has(name));
}

/**
 * Prunes the registry tree so gated measurements the user has not enabled are
 * removed entirely. Used to drive UI that lists the full measurement/check tree
 * (e.g. the "Measurements & checks" filter dropdown) so a gated measurement does
 * not appear as a selectable option while its flag is off. A measurement is
 * dropped only when ALL of its checks are hidden; categories left with no
 * visible measurements are dropped too. Returns the input unchanged when nothing
 * is gated.
 */
export function pruneGatedMeasurements(
  registryData: CategoryNode[],
  isFlagActive: IsFlagActive,
  gatedMeasurements: GatedMeasurement[] = GATED_MEASUREMENTS,
): CategoryNode[] {
  const hidden = getHiddenCheckNames(
    registryData,
    isFlagActive,
    gatedMeasurements,
  );
  if (hidden.size === 0) {
    return registryData;
  }
  return registryData
    .map(category => ({
      ...category,
      measurements: category.measurements.filter(
        measurement =>
          !measurement.checks.every(check => hidden.has(check.name)),
      ),
    }))
    .filter(category => category.measurements.length > 0);
}
